/**
 * Edit Mode Service for Banner Builder
 *
 * Controls what users can edit in layouts based on the current edit mode.
 * Four modes available:
 * - FULL_EDIT: Everything (default)
 * - TEXT_PRODUCT_IMAGE: Copy, product images, decorative elements (backgrounds, shapes)
 * - TEXT_PRODUCT: Copy and product images only (no background/image changes)
 * - PRODUCT_ONLY: Product images only (for licensing like FIFA/UCL)
 */

import {
  EDIT_MODE,
  EDIT_MODE_LABELS,
  ALWAYS_EDITABLE_PREFIXES,
  ALWAYS_EDITABLE_FIELD_TYPES,
  TEXT_EDITABLE_PREFIXES,
  TEXT_EDITABLE_FIELD_TYPES,
  DECORATIVE_FIELD_TYPES,
  DECORATIVE_EDITABLE_PREFIXES,
} from '@frontend/constants/edit-mode';

class EditModeService {
  constructor() {
    // Initialize with saved mode from page load, or default to FULL_EDIT
    this._editMode = window.layoutEditMode || EDIT_MODE.FULL_EDIT;
  }

  /**
   * Set the current edit mode
   * @param {string} mode - One of EDIT_MODE values
   */
  setEditMode(mode) {
    this._editMode = mode || EDIT_MODE.FULL_EDIT;
  }

  /**
   * Get the current edit mode
   * @returns {string}
   */
  getEditMode() {
    return this._editMode;
  }

  /**
   * Get the human-readable label for the current edit mode
   * @returns {string}
   */
  getEditModeLabel() {
    return EDIT_MODE_LABELS[this._editMode] || this._editMode;
  }

  /**
   * Check if any restrictions are active (not full edit mode)
   * @returns {boolean}
   */
  isRestricted() {
    return this._editMode !== EDIT_MODE.FULL_EDIT;
  }

  /**
   * Check if the current mode is Full Edit
   * @returns {boolean}
   */
  isFullEdit() {
    return this._editMode === EDIT_MODE.FULL_EDIT;
  }

  /**
   * Determine if an element can be edited based on current edit mode
   * @param {fabric.Object|Object} element - The Fabric.js object or element data
   * @returns {boolean} - True if element can be edited
   */
  canEditElement(element) {
    if (!this.isRestricted()) {
      return true; // Full edit mode, everything editable
    }

    const fieldType = this._getFieldType(element);
    const elementId = this._getElementId(element);

    // Product images are ALWAYS editable in ALL modes
    if (this._isProductImage(fieldType, elementId, element)) {
      return true;
    }

    // In PRODUCT_ONLY mode, only product images are editable
    if (this._editMode === EDIT_MODE.PRODUCT_ONLY) {
      return false;
    }

    // In TEXT_PRODUCT mode, only text and product images are editable (no decorative)
    if (this._editMode === EDIT_MODE.TEXT_PRODUCT) {
      if (this._isTextElement(fieldType, elementId)) {
        return true;
      }
      return false;
    }

    // In TEXT_PRODUCT_IMAGE mode, check if element is text or decorative
    if (this._editMode === EDIT_MODE.TEXT_PRODUCT_IMAGE) {
      if (this._isTextElement(fieldType, elementId)) {
        return true;
      }
      if (this._isDecorativeElement(fieldType, elementId)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Determine if structural modifications are allowed for an element
   * (move, resize, rotate)
   * @param {fabric.Object|Object} element
   * @returns {boolean}
   */
  allowsStructuralChanges(element) {
    if (!this.isRestricted()) {
      return true; // Full edit mode, all structural changes allowed
    }

    // In restricted modes, structural changes are generally not allowed
    // except for product images which can be adjusted within their bounds
    const fieldType = this._getFieldType(element);
    const elementId = this._getElementId(element);

    if (this._isProductImage(fieldType, elementId, element)) {
      return true; // Product images can be repositioned/resized
    }

    return false;
  }

  /**
   * Determine if content editing is allowed (text editing, image swapping)
   * @param {fabric.Object|Object} element
   * @returns {boolean}
   */
  allowsContentEditing(element) {
    if (!this.isRestricted()) {
      return true;
    }

    const fieldType = this._getFieldType(element);
    const elementId = this._getElementId(element);

    // Product images are always swappable
    if (this._isProductImage(fieldType, elementId, element)) {
      return true;
    }

    // Text is editable in TEXT_PRODUCT and TEXT_PRODUCT_IMAGE modes
    if (this._editMode === EDIT_MODE.TEXT_PRODUCT || this._editMode === EDIT_MODE.TEXT_PRODUCT_IMAGE) {
      if (this._isTextElement(fieldType, elementId)) {
        return true;
      }
    }

    // Decorative elements only editable in TEXT_PRODUCT_IMAGE mode
    if (this._editMode === EDIT_MODE.TEXT_PRODUCT_IMAGE) {
      if (this._isDecorativeElement(fieldType, elementId)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Get the edit restrictions for an element (for UI display)
   * @param {fabric.Object|Object} element
   * @returns {Object} - { canEdit, allowsStructuralChanges, allowsContentEditing, reason }
   */
  getElementRestrictions(element) {
    const fieldType = this._getFieldType(element);
    const elementId = this._getElementId(element);
    const canEdit = this.canEditElement(element);

    return {
      canEdit,
      allowsStructuralChanges: this.allowsStructuralChanges(element),
      allowsContentEditing: this.allowsContentEditing(element),
      reason: this._getRestrictionReason(fieldType, elementId, canEdit, element),
      editMode: this._editMode,
      editModeLabel: this.getEditModeLabel(),
    };
  }

  /**
   * Get field type from element
   * @private
   */
  _getFieldType(element) {
    if (!element) return '';
    return element.fieldType || element.templateField?.type || element.type || '';
  }

  /**
   * Get element ID from element
   * @private
   */
  _getElementId(element) {
    if (!element) return '';
    return element.element_id || element.id || '';
  }

  /**
   * Check if element is a product image
   * @private
   */
  _isProductImage(fieldType, elementId, element = null) {
    // Check explicit field types ONLY - no fallback logic
    if (ALWAYS_EDITABLE_FIELD_TYPES.includes(fieldType)) {
      return true;
    }

    // Check element ID prefixes (e.g., "image_1", "add_image_2")
    if (ALWAYS_EDITABLE_PREFIXES.some(prefix =>
      elementId.toLowerCase().startsWith(prefix)
    )) {
      return true;
    }

    // NO FALLBACK - if not explicitly marked as Product Image, it's not
    return false;
  }

  /**
   * Check if element is a text element
   * @private
   */
  _isTextElement(fieldType, elementId) {
    if (TEXT_EDITABLE_FIELD_TYPES.includes(fieldType)) {
      return true;
    }
    return TEXT_EDITABLE_PREFIXES.some(prefix =>
      elementId.toLowerCase().startsWith(prefix)
    );
  }

  /**
   * Check if element is a decorative element
   * @private
   */
  _isDecorativeElement(fieldType, elementId) {
    if (DECORATIVE_FIELD_TYPES.includes(fieldType)) {
      return true;
    }
    return DECORATIVE_EDITABLE_PREFIXES.some(prefix =>
      elementId.toLowerCase().startsWith(prefix)
    );
  }

  /**
   * Get human-readable restriction reason
   * @private
   */
  _getRestrictionReason(fieldType, elementId, canEdit, element = null) {
    if (!this.isRestricted()) {
      return '';
    }

    if (canEdit) {
      if (this._isProductImage(fieldType, elementId, element)) {
        return 'Product images can be swapped';
      }
      if (this._isTextElement(fieldType, elementId)) {
        return 'Text content can be edited';
      }
      if (this._isDecorativeElement(fieldType, elementId)) {
        return 'Decorative elements can be modified';
      }
      return '';
    }

    // Element is restricted
    if (this._editMode === EDIT_MODE.PRODUCT_ONLY) {
      return 'Product-Only mode: Only product images can be changed';
    }

    if (this._editMode === EDIT_MODE.TEXT_PRODUCT) {
      return 'Text + Product mode: Only text and product images can be changed';
    }

    if (this._editMode === EDIT_MODE.TEXT_PRODUCT_IMAGE) {
      return 'This element is part of the locked layout structure';
    }

    return 'This element cannot be edited in the current mode';
  }
}

// Singleton instance
export const editModeService = new EditModeService();

export default editModeService;
